import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Col,
  Button,
  Form,
  Row,
  Breadcrumb,
  Spinner,
  InputGroup,
  Modal,
  Accordion,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faIdBadge, faBriefcase, faCogs, faArrowRight, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import FalconCloseButton from "components/common/falconCloseButton";
import DatePicker from "react-datepicker";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppContext } from "providers/appProvider";
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
  useLocationRedirect,
} from "../../assets/js/common";
import "react-phone-number-input/style.css";
import { getCountries } from "react-phone-number-input";
import { getExampleNumber, getCountryCallingCode } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import moment from "moment";
import Select from "react-select";
import paths from "routes/paths";
import MultiSelect from "components/common/multiSelect";
import { getDomainConfig } from "../../config/domainConfig";
import { rootPaths } from "routes/paths";
import CustomCountrySelect from "components/common/CustomCountrySelect";
import { ROLE_NAMES } from "../../config/roleConfig";

const NewUser = () => {
  const { t, i18n } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subModalData, setSubModalData] = useState({ allowed: false, isTrialWarning: false, message: null });
  const [maxUsers, setMaxUsers] = useState(null);
  const culture = localStorage.getItem("culture");
  const maxUsersRef = useRef(null);
  const subscriptionRef = useRef("");
  const [specialities, setSpecialities] = useState([]);

  const steps = [
    { id: 1, title: t("personalDetails"), subtitle: t("personalDetailsSub") },
    { id: 2, title: t("accountInformation"), subtitle: t("accountInformationSub") },
    { id: 3, title: t("employmentDetails"), subtitle: t("employmentDetailsSub") },
    { id: 4, title: t("administration"), subtitle: t("administrationSub") },
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const [activeAccordionKey, setActiveAccordionKey] = useState("1");
  const [managers, setManagers] = useState([]);

  const stepFields = {
    1: ["name", "phoneNumber", "email"],
    2: ["role", "locationIds", "managerId"],
    3: ["dateOfJoining", "features", "employmentType", "contractStartDate", "contractEndDate", "contractNotes"],
    4: ["fixedHours", "hourlyRate", "weekdays", "salary", "salaryPeriod"],
  };

  const requiredFieldsByStep = {
    1: ["name", "email"],
    2: ["role", "locationIds"],
    3: ["employmentType"],
    4: [],
  };

  // Validation Schema
  const schema = yup
    .object({
      name: yup.string().required(t("nameRequired")), // Name is required
      role: yup.string().required(t("roleRequired")), // Type is required
      // status: yup.string().required(t("statusRequired")), // Status is required
      email: yup.string().email(t("invalidEmail")).required(t("emailRequired")),
      phoneNumber: yup
        .string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .test("has-country-code", t("enterValidMobileNumber"), function (value) {
          if (!value) return true;
          if (!value.startsWith("+")) return false;
          const digitsOnly = value.replace(/[^\d]/g, "");
          return digitsOnly.length > 0 && digitsOnly.length <= 20;
        }),
      locationIds: yup
        .array()
        .min(1, t("branchRequired"))
        .required(t("branchRequired")),
      dateOfJoining: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .typeError(t("invalidDate"))
        .when("employmentType", {
          is: "permanent",
          then: (s) => s.required(t("dateOfJoiningRequired")),
        }),
      features: yup
        .string()
        .transform((value) => (value === "" ? null : value))
        .nullable(), // Make 'features' nullable in the schema
      hourlyRate: yup
        .number()
        .nullable()
        .transform((v, orig) => (orig === "" ? null : v))
        .typeError(t("invalidRate"))
        .min(0, t("invalidRate"))
        .max(99999.99, t("invalidRate")),
      fixedHours: yup
        .number()
        .nullable()
        .transform((v, orig) => (orig === "" || orig === null || orig === undefined ? null : v))
        .typeError(t("invalidHours"))
        .min(0, t("invalidHours"))
        .max(999.99, t("invalidHours")),
      managerId: yup
        .string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .when("role", {
          is: (roleVal) => !!roleVal && !nonMandatoryManagerRoleIds.includes(roleVal),
          then: (s) => s.required(t("managerRequired")),
        }),
      employmentType: yup.string().required(t("employmentTypeRequired")),
      contractStartDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .when("employmentType", {
          is: "contract",
          then: (s) => s.required(t("contractStartRequired")),
        }),
      contractEndDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .when("employmentType", {
          is: "contract",
          then: (s) =>
            s
              .required(t("contractEndRequired"))
              .min(yup.ref("contractStartDate"), t("contractEndAfterStart")),
        }),
      contractNotes: yup.string().nullable(),
      salary: yup
        .number()
        .nullable()
        .transform((v, orig) => (orig === "" ? null : v)),
      salaryPeriod: yup
        .string()
        .nullable(),
    })
    .required(); // Entire schema is required

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      country: "Nederland",
      dateOfJoining: null,
      fixedHours: "8",
      hourlyRate: "",
      weekdays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      },
      managerId: "",
      employmentType: "permanent",
      contractStartDate: null,
      contractEndDate: null,
      contractNotes: "",
      salary: "",
      salaryPeriod: "monthly",
    },
  });

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = methods;

  const watchedValues = watch();

  useEffect(() => {
    const type = watchedValues.employmentType;
    if (!type) return;
    const newDays = type === "permanent"
      ? ["monday", "tuesday", "wednesday", "thursday", "friday"]
      : ["monday"];
    setSelectedDays(newDays);
    dayKeys.forEach((day) => {
      setValue(`weekdays.${day}`, newDays.includes(day));
    });
  }, [watchedValues.employmentType]);

  useEffect(() => {
    const type = watchedValues.employmentType;
    if (type === "permanent" && !watchedValues.salaryPeriod) {
      setValue("salaryPeriod", "monthly");
    }
  }, [watchedValues.employmentType]);

  useEffect(() => {
    setCompletedSteps((prev) =>
      prev.filter((stepId) => {
        const requiredFields = requiredFieldsByStep[stepId] || [];
        if (requiredFields.length === 0) return true;
        return requiredFields.every((field) => {
          const val = watchedValues[field];
          if (field === "locationIds") return Array.isArray(val) && val.length > 0;
          return val !== undefined && val !== null && val !== "";
        });
      })
    );
  }, [watchedValues]);

  const otherStepsCompleted = useMemo(() => {
    return [1, 2, 3, 4]
      .filter((id) => id !== currentStep)
      .every((id) => completedSteps.includes(id));
  }, [completedSteps, currentStep]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [dateOfJoining, setDateOfJoining] = useState(null);
  const [isJoinDateOpen, setIsJoinDateOpen] = useState(false);
  const [isContractStartOpen, setIsContractStartOpen] = useState(false);
  const [isContractEndOpen, setIsContractEndOpen] = useState(false);
  const [data, setData] = useState([]);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const {
    config: { location },
  } = useAppContext();
  const [hasAccess, setHasAccess] = useState(false);
  const [selectedDays, setSelectedDays] = useState(["monday", "tuesday", "wednesday", "thursday", "friday"]);
  const [userLocations, setuserLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const dayKeys = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const domainConfig = getDomainConfig();
  const currencySymbol = domainConfig.financialLabels.currencySymbol;
  const [countryCode, setCountryCode] = useState(domainConfig.defaultPhoneCountry);
  const defaultCountryCode = (domainConfig.defaultPhoneCountry || "in").toUpperCase();
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(defaultCountryCode);

  const phonePlaceholder = useMemo(() => {
    const example = getExampleNumber(selectedPhoneCountry, examples);
    return example ? example.formatNational() : "";
  }, [selectedPhoneCountry]);

  const nonMandatoryManagerRoleIds = useMemo(
    () =>
      data
        .filter((r) => r.Name === ROLE_NAMES.APP_OWNER || r.Name === ROLE_NAMES.CLIENT)
        .map((r) => r.RoleId),
    [data]
  );

  const callingCode = useMemo(() => {
    try {
      return getCountryCallingCode(selectedPhoneCountry);
    } catch {
      return "";
    }
  }, [selectedPhoneCountry]);

  const maxNationalLength = useMemo(() => {
    const example = getExampleNumber(selectedPhoneCountry, examples);
    return example ? example.nationalNumber.length : 15;
  }, [selectedPhoneCountry]);

    const countrySelectOptions = useMemo(() => {
    return getCountries().map((country) => ({
      value: country,
      label: country,
    }));
  }, []);

  useLocationRedirect("users");
  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const fetchLocations = async () => {
    setLoading(true);
    const customerId = localStorage.getItem("customerId");
    if (!customerId) {
      setLoading(false);
      setError("CustomerId is missing.");
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/locationRoute/all`, {
        params: { customerId },
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setuserLocations(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch branches. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionAndCount = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      console.log("[fetchSubscriptionAndCount] customerId →", customerId);

      const [customerRes, countRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}/getActiveUsersCount`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        ),
      ]);

      const plan = (customerRes.data?.Subscription?.SubscriptionType ?? "").toLowerCase();
      const max = customerRes.data?.Subscription?.MaxUsers ?? null;

      const activeCount = countRes.data?.ActiveUserCount ?? 0;

      setSubscription(plan);
      setMaxUsers(max);
      setActiveUserCount(activeCount);

      subscriptionRef.current = plan;
      maxUsersRef.current = max;

    } catch (err) {
      console.error("[fetchSubscriptionAndCount] failed:", err);
      setSubscription("");
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchSubscriptionAndCount();
  }, []);

  useEffect(() => {
    const fetchSpecialities = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/specialityRoute/byCustomer/${customerId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        setSpecialities(response.data);
      } catch (err) {
        console.error("Error fetching specialities:", err);
      }
    };
    fetchSpecialities();
  }, []);

  const handleDiscard = () => {
    reset();
    navigate("/users"); //redirect to overview table
  };

  const handleNext = async () => {
    const valid = await trigger(stepFields[currentStep]);
    if (!valid) {
      toast.error(t("pleaseFillRequiredFields"), { position: "top-right", autoClose: 3000 });
      return;
    }
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    setCurrentStep((step) => {
      const nextStep = Math.min(step + 1, 4);
      setActiveAccordionKey(String(nextStep));
      return nextStep;
    });
  };

  const handleSave = async () => {
    const valid = await trigger(stepFields[currentStep]);
    if (valid) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    }
    handleSubmit(onSubmit, onInvalid)();
  };

  const onInvalid = (formErrors) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length === 0) return;

    const stepWithError = Object.keys(stepFields).find((stepId) =>
      stepFields[stepId].some((field) => errorFields.includes(field))
    );

    if (stepWithError) {
      setCurrentStep(Number(stepWithError));
      setActiveAccordionKey(stepWithError);
    }

    toast.error(t("pleaseFillRequiredFields"), {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const handleBack = () => {
    setCurrentStep((step) => {
      const prevStep = Math.max(step - 1, 1);
      setActiveAccordionKey(String(prevStep));
      return prevStep;
    });
  };

  const goToStep = (stepId) => {
    if (stepId === currentStep) return;
    setCurrentStep(stepId);
    setActiveAccordionKey(String(stepId));
  };

  useEffect(() => {
    if (userLocations.length > 0 && selectedLocations.length === 0) {
      const activeLocationId =
        location?.LocationId || localStorage.getItem("LocationId");

      const matchedLocation = userLocations.find(
        (loc) => String(loc.LocationId) === String(activeLocationId)
      );

      const defaultLocationId = matchedLocation
        ? matchedLocation.LocationId
        : userLocations[0].LocationId;

      setSelectedLocations([defaultLocationId]);
      setValue("locationIds", [defaultLocationId]);
      queueMicrotask(() => trigger("locationIds"));
    }
  }, [userLocations, location]);

  const generateUserNumber = async () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0"); // Day padded with zero
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Month padded with zero
    const year = String(now.getFullYear()).slice(-2); // Last two digits of the year
    const hour = String(now.getHours()).padStart(2, "0"); // Hour padded with zero
    const minute = String(now.getMinutes()).padStart(2, "0"); // Minute padded with zero

    return `UL${day}${month}${year}${hour}${minute}`; // Constructed relation number
  };

  const roleTranslations = {
    [ROLE_NAMES.APP_OWNER]: "App beheerder",
    [ROLE_NAMES.ADMIN]: "Admin",
    [ROLE_NAMES.CLIENT]: "Klant",
    [ROLE_NAMES.EMPLOYEE]: "Medewerker",
  };

  const generateTempPassword = (length = 12) => {
    if (length < 8) length = 8; // minimum length

    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const special = "!@#$%^&*";
    const allChars = lower + upper + digits + special;

    let password = "";
    // Ensure at least one from each set
    password += lower[Math.floor(Math.random() * lower.length)];
    password += upper[Math.floor(Math.random() * upper.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle
    password = password.split("").sort(() => Math.random() - 0.5).join("");
    return password;
  };

  const generateToken = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  const validateUserCreation = (plan, activeCount, maxUsers) => {
    const planKey = (plan ?? "").toLowerCase();

    if (planKey === "enterprise") {
      return { allowed: true, isTrialWarning: false, message: null, showEmail: false };
    }

    if (maxUsers === null) {
      return {
        allowed: false,
        isTrialWarning: false,
        showEmail: false,
        message: t("subscriptionDataNotLoaded"),
      };
    }

    if (planKey === "trial") {
      if (activeCount >= maxUsers) {
        return { allowed: false, isTrialWarning: false, showEmail: false, message: t("planLimitReached") };
      }
      return { allowed: true, isTrialWarning: true, showEmail: false, message: t("trialUserWarning") };
    }

    if (planKey === "basic") {
      if (activeCount >= maxUsers) {
        return { allowed: false, isTrialWarning: false, showEmail: false, message: t("planLimitReached") };
      }
      return { allowed: true, isTrialWarning: false, message: null, showEmail: false };
    }

    if (planKey === "standard") {
      if (activeCount >= maxUsers) {
        return { allowed: false, isTrialWarning: false, showEmail: true, message: t("standardLimitReached") };
      }
      return { allowed: true, isTrialWarning: false, message: null, showEmail: false };
    }

    return { allowed: true, isTrialWarning: false, message: null, showEmail: false };
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const customerId = localStorage.getItem("customerId");

      const freshCountRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}/getActiveUsersCount`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      ).catch(() => null);

      const freshCount = freshCountRes?.data?.ActiveUserCount ?? activeUserCount;

      const currentPlan = subscriptionRef.current;
      const currentMaxUsers = maxUsersRef.current;

      const { allowed, isTrialWarning, message, showEmail } = validateUserCreation(
        currentPlan,
        freshCount,
        currentMaxUsers
      );

      if (!allowed) {
        setSubModalData({ allowed: false, isTrialWarning: false, message, showEmail });
        setShowSubModal(true);
        setIsLoading(false);
        return;
      }

      if (isTrialWarning) {
        setSubModalData({ allowed: true, isTrialWarning: true, message, showEmail });
        setShowSubModal(true);
        setIsLoading(false);
        return;
      }

      await submitUser(data);

    } catch (err) {
      console.error("[onSubmit] unexpected error:", err);
      toast.error(t("errorOccurred"), { position: "top-right", autoClose: 5000 });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    subscriptionRef.current = subscription;
  }, [subscription]);

  useEffect(() => {
    maxUsersRef.current = maxUsers;
  }, [maxUsers]);

  const submitUser = async (data) => {
    setIsLoading(true);
    const tempPassword = generateTempPassword();
    try {
      const userId = localStorage.getItem("UserId");
      const culture = localStorage.getItem("culture");
      const customerId = localStorage.getItem("customerId");
      const userNumber = await generateUserNumber();
      const hashedPassword = await hashPassword(tempPassword);
      const formattedDate = data.dateOfJoining
        ? moment(data.dateOfJoining).format("YYYY-MM-DD")
        : null;
      const nowUtc = moment.utc().format("YYYY-MM-DD HH:mm:ss");

      const updatedFormData = {
        ...data,
        weekdays: selectedDays.length > 0 ? selectedDays : null,
        dateOfJoining: formattedDate,
        managerId: data.managerId || null,
        employmentType: data.employmentType,
        contractStartDate: data.employmentType === "contract" && data.contractStartDate
          ? moment(data.contractStartDate).format("YYYY-MM-DD")
          : null,
        contractEndDate: data.employmentType === "contract" && data.contractEndDate
          ? moment(data.contractEndDate).format("YYYY-MM-DD")
          : null,
        contractNotes: data.employmentType === "contract" ? data.contractNotes || null : null,
        hourlyRate: data.employmentType === "contract" ? data.hourlyRate || null : null,
        salary: data.employmentType === "permanent" ? data.salary : null,
        salaryPeriod: data.employmentType === "permanent" ? data.salaryPeriod : null,
        password: hashedPassword,
        userNumber,
        locationId: location?.LocationId || null,
        customerId: customerId || null,
        status: 0,
        activeDateTime: nowUtc,
        inactiveDateTime: null,
        isDelete: false,
        isCustomer: false,
        defaultLanguage: culture ? culture.toUpperCase() : "NL",
        performedBy: userId || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/userRoute/addUser`,
        updatedFormData,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      if (response.status === 201) {
        const createdUserId = response.data.UserId;
        const cultureLocal = localStorage.getItem("culture") || "nl";

        if (selectedLocations.length > 0) {
          for (const locationId of selectedLocations) {
            await axios.post(
              `${process.env.REACT_APP_API_URL}/userLocationsRoute/save`,
              {
                UserId: createdUserId,
                CustomerId: customerId,
                LocationId: locationId,
                performedBy: userId || null,
              },
              { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
          }
        }

        await axios.post(
          `${process.env.REACT_APP_API_URL}/userSpecialitiesRoute/save`,
          {
            UserId: createdUserId,
            CustomerId: customerId,
            SpecialityIds: data.specialityIds || [],
          },
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        try {
          const token = generateToken();
          const resetLink = `${window.location.origin}${paths.resetPassword}?token=${token}&culture=${cultureLocal}`;
          const adminEmail = localStorage.getItem("UserEmail");

          await axios.post(
            `${process.env.REACT_APP_API_URL}/tokenDataRoute/createToken`,
            { token, userId: createdUserId, tokenUsed: false },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );

          await axios.post(
            `${process.env.REACT_APP_API_URL}/emailRoute/userCreatedEmail`,
            {
              loggedInUserId: userId,
              customerId,
              userId: createdUserId,
              resetLink,
              tempPassword,
              adminEmail,
              culture: cultureLocal,
            },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
        } catch (emailError) {
          console.warn("Email sending failed:", emailError.response?.data);
        }

        reset();
        toast.success(t("userAdded"), { position: "top-right", autoClose: 5000 });
        navigate("/users");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      console.error("Backend error response:", error.response?.data);
      const messageKey = error.response?.data?.messageKey || "errorOccurred";

      toast.error(t(messageKey), {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "users",
      claimValue: "create",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const fetchedRoles = response.data;
        const desiredOrder = [
          t(`roles.${ROLE_NAMES.APP_OWNER}`),
          t(`roles.${ROLE_NAMES.ADMIN}`),
          t(`roles.${ROLE_NAMES.CLIENT}`),
          t(`roles.${ROLE_NAMES.EMPLOYEE}`),
        ];
        const sortedRoles = fetchedRoles.sort((a, b) => {
          const nameA = t(`roles.${a.Name}`);
          const nameB = t(`roles.${b.Name}`);
          return desiredOrder.indexOf(nameA) - desiredOrder.indexOf(nameB);
        });

        setData(sortedRoles);

        // Default role = Employee
        const employeeRole = sortedRoles.find((role) => role.Name === ROLE_NAMES.EMPLOYEE);
        if (employeeRole) {
          setValue("role", employeeRole.RoleId, { shouldValidate: true });
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const locationIdStored = localStorage.getItem("LocationId");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/eligibleManagers`,
          {
            params: { locationId: locationIdStored, targetRoleId: watchedValues.role },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        setManagers(response.data);
      } catch (err) {
        console.error("Error fetching managers:", err);
      }
    };
    if (watchedValues.role) {
      fetchManagers();
    }
  }, [watchedValues.role]);

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) => {
      const newSelected = prev.includes(dayKey)
        ? prev.filter((d) => d !== dayKey)
        : [...prev, dayKey];

      // Update the individual checkbox value
      setValue(`weekdays.${dayKey}`, !prev.includes(dayKey));
      return newSelected;
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background
                image={corner4}
                className="bg-card"
                style={{
                  borderTopRightRadius: "0.375rem",
                  borderBottomRightRadius: "0.375rem",
                }}
              />
              <Card.Body className="position-relative pe-4 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">{t("newUser")}</h5>
                    <Breadcrumb>
                      <Breadcrumb.Item
                        as={Link}
                        to="/users"
                        className="fw-semi-bold mb-1 fs-9 data-font-colour"
                        active
                      >
                        {t("users")}
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        className="fw-bold mb-1 fs-9 data-font-colour"
                        active
                      >
                        {t("new")}
                      </Breadcrumb.Item>
                    </Breadcrumb>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-stretch">
            {/* Left Sidebar */}
            <Col xs="12" lg="3" xl="2" className="rf-left-panel step-sidebar-col">
              <Card className="mb-3 h-100 step-row-container">
                <Card.Body>
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      lang={i18n.language}
                      className={`d-flex gap-2 pointer p-2 rounded step-row ${currentStep === step.id ? "step-row-active" : ""
                        }`}
                      onClick={() => goToStep(step.id)}
                    >
                      <div className="d-flex flex-column align-items-center step-circle-col">
                        <span
                          className={`step-circle ${currentStep === step.id ? "active" : ""} ${completedSteps.includes(step.id) ? "completed" : ""}`}>
                          {step.id}
                        </span>
                        {index < steps.length - 1 && (
                          <div className={`step-connector-line ${completedSteps.includes(step.id) ? "completed" : ""} ${currentStep === step.id ? "active" : ""}`} />
                        )}
                      </div>
                      <div className="step-text-col">
                        <h5 className={`fw-medium ${currentStep === step.id ? "text-primary" : ""}`}>
                          {step.title}
                        </h5>
                        <small className="mt-2 text-secondary">{step.subtitle}</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
            {/* Right Side Form */}
            <Col xs="12" lg="9" xl="10" className="rf-right-panel rf-form-col">
              <Accordion
                activeKey={activeAccordionKey}
                onSelect={(key) => { if (key) goToStep(Number(key)); }}
                className="rf-accordion"
              >
                <Accordion.Item eventKey="1" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-client"><FontAwesomeIcon icon={faUser} /></span>
                      <span className="rf-header-text">
                        <span>{t("personalDetails")}</span>
                        <span className="rf-header-subtitle">{t("personalDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("name")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            size="md"
                            placeholder={("John Doe")}
                            {...register("name")}
                            isInvalid={!!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="6" lg="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("emailAddress")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            placeholder={("john.doe@example.com")}
                            {...register("email")}
                            isInvalid={!!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("mobileNumber")}
                          </Form.Label>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value, ref } }) => {
                              const prefix = `+${callingCode}`;
                              const displayValue =
                                value && value.startsWith(prefix)
                                  ? value.slice(prefix.length)
                                  : value || "";

                              return (
                                <div className={`phone-field-wrapper ${errors.phoneNumber ? "is-invalid" : ""}`}>
                                  <span className="phone-country-select-slot">
                                    <CustomCountrySelect
                                      value={selectedPhoneCountry}
                                      options={countrySelectOptions}
                                      onChange={(country) => {
                                        if (!country) return;
                                        const oldPrefix = `+${callingCode}`;
                                        const rawDigits =
                                          value && value.startsWith(oldPrefix)
                                            ? value.slice(oldPrefix.length)
                                            : value || "";
                                        let newCallingCode = "";
                                        try {
                                          newCallingCode = getCountryCallingCode(country);
                                        } catch {
                                          newCallingCode = "";
                                        }
                                        setSelectedPhoneCountry(country);
                                        onChange(rawDigits ? `+${newCallingCode}${rawDigits}` : "");
                                      }}
                                    />
                                  </span>
                                  <span className="phone-code-display">+{callingCode}</span>
                                  <input
                                    ref={ref}
                                    type="text"
                                    className="phone-number-input"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      onChange(raw ? `${prefix}${raw}` : "");
                                    }}
                                    placeholder={phonePlaceholder}
                                    maxLength={25}
                                    name="phoneNumber"
                                  />
                                </div>
                              );
                            }}
                          />
                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}
                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}
                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-address"><FontAwesomeIcon icon={faIdBadge} /></span>
                      <span className="rf-header-text">
                        <span>{t("accountInformation")}</span>
                        <span className="rf-header-subtitle">{t("accountInformationSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="4" lg="4" className="position-relative z-3">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("roleText")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="role"
                            control={control}
                            rules={{ required: t("roleRequired") }}
                            render={({ field, fieldState: { error } }) => {
                              const options = data
                                .filter((role) => role.Name !== ROLE_NAMES.CLIENT)
                                .map((role) => ({
                                  value: role.RoleId,
                                  label: t(`roles.${role.Name}`),
                                }));

                              return (
                                <>
                                  <Select
                                    key={i18n.language}
                                    {...field}
                                    options={options}
                                    isClearable
                                    value={options.find((opt) => opt.value === field.value) || null}
                                    onChange={(selected) => {
                                      const selectedValue = selected ? selected.value : "";
                                      field.onChange(selectedValue);
                                      setValue("role", selectedValue);
                                    }}
                                    onBlur={field.onBlur}
                                    className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                    classNamePrefix="custom-select"
                                    placeholder={t("select")}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                  />
                                  {error && (
                                    <div className="invalid-feedback d-block">
                                      {error.message}
                                    </div>
                                  )}
                                </>
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md="6" lg="6" xl="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("branch")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <MultiSelect
                            isMulti
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                            options={userLocations.map(location => ({
                              value: location.LocationId,
                              label: location.Name,
                            }))}

                            value={userLocations
                              .filter(loc => selectedLocations.includes(loc.LocationId))
                              .map(loc => ({
                                value: loc.LocationId,
                                label: loc.Name,
                              }))}

                            classNamePrefix="react-select"
                            className={errors.locationIds ? "is-invalid" : ""}

                            onChange={(selected) => {
                              const values = selected ? selected.map(item => item.value) : [];
                              setSelectedLocations(values);
                              setValue("locationIds", values, { shouldValidate: true });
                            }}
                          />
                          {errors.locationIds && (
                            <div className="invalid-feedback d-block">
                              {errors.locationIds.message}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("manager")}
                            {watchedValues.role && !nonMandatoryManagerRoleIds.includes(watchedValues.role) && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Controller
                            name="managerId"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Select
                                  options={managers.map((m) => ({ value: m.UserId, label: m.Name }))}
                                  isClearable
                                  value={managers
                                    .map((m) => ({ value: m.UserId, label: m.Name }))
                                    .find((opt) => opt.value === field.value) || null}
                                  onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                  className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                  classNamePrefix="custom-select"
                                  placeholder={t("select")}
                                  noOptionsMessage={() => t("noOptionsAvailable")}
                                />
                                {error && (
                                  <div className="invalid-feedback d-block">{error.message}</div>
                                )}
                              </>
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-contact"><FontAwesomeIcon icon={faBriefcase} /></span>
                      <span className="rf-header-text">
                        <span>{t("employmentDetails")}</span>
                        <span className="rf-header-subtitle">{t("employmentDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="2" lg="2">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour text-nowrap">
                            {t("employmentType")}<span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="employmentType-permanent"
                              name="employmentType"
                              label={t("permanent")}
                              checked={watch("employmentType") === "permanent"}
                              onChange={() => setValue("employmentType", "permanent", { shouldValidate: true })}
                            />
                            <Form.Check
                              type="radio"
                              id="employmentType-contract"
                              name="employmentType"
                              label={t("contract")}
                              checked={watch("employmentType") === "contract"}
                              onChange={() => setValue("employmentType", "contract", { shouldValidate: true })}
                            />
                          </div>
                          {errors.employmentType && (
                            <div className="invalid-feedback d-block">{errors.employmentType.message}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {watch("employmentType") === "permanent" && (
                      <>
                        <Row className="mb-3">
                          <Col md="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour text-nowrap">
                                {t("employeeJoinDate")}<span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="dateOfJoining"
                                defaultValue={null}
                                render={({ field }) => (
                                  <div className={`position-relative date-picker-wrapper ${errors.dateOfJoining ? "is-invalid-wrapper" : ""}`}>
                                    <DatePicker
                                      ref={field.ref}
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsJoinDateOpen(false);
                                      }}
                                      onBlur={field.onBlur}
                                      open={isJoinDateOpen}
                                      onInputClick={() => setIsJoinDateOpen(true)}
                                      onClickOutside={() => {
                                        setIsJoinDateOpen(false);
                                        field.onBlur();
                                      }}
                                      dateFormat="dd-MM-yyyy"
                                      className={`form-control pe-2 py-2 ${errors.dateOfJoining ? "is-invalid" : ""}`}
                                      placeholderText="dd-mm-yyyy"
                                    />    
                                    <span
                                      className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                      onClick={() => setIsJoinDateOpen((prev) => !prev)}
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                    </span>
                                  </div>
                                )}
                              />
                              {errors.dateOfJoining && (
                                <Form.Control.Feedback type="invalid" className="d-block text-nowrap">
                                  {errors.dateOfJoining.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md="5">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">{t("speciality")}</Form.Label>
                              <Controller
                                name="specialityIds"
                                control={control}
                                defaultValue={[]}
                                size="md"
                                render={({ field }) => (
                                  <MultiSelect
                                    isMulti
                                    closeMenuOnSelect={false}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                    options={specialities.map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    value={specialities
                                      .filter(s => (field.value || []).includes(s.SpecialityId))
                                      .map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    classNamePrefix="react-select"
                                    onChange={(selected) => field.onChange(selected ? selected.map(o => o.value) : [])}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    {watch("employmentType") === "contract" && (
                      <>
                        <Row className="mb-3">
                          <Col md="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractStartDate")}<span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="contractStartDate"
                                defaultValue={null}
                                render={({ field }) => (
                                  <div className={`position-relative date-picker-wrapper ${errors.contractStartDate ? "is-invalid-wrapper" : ""}`}>
                                    <DatePicker
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsContractStartOpen(false);
                                        const currentEndDate = watch("contractEndDate");
                                        if (currentEndDate) {
                                          if (date && currentEndDate <= date) {
                                            setValue("contractEndDate", null);
                                          } else {
                                            trigger("contractEndDate");
                                          }
                                        }
                                      }}
                                      open={isContractStartOpen}
                                      onInputClick={() => setIsContractStartOpen(true)}
                                      onClickOutside={() => setIsContractStartOpen(false)}
                                      dateFormat="dd-MM-yyyy"
                                      className={`form-control pe-2 py-2 ${errors.contractStartDate ? "is-invalid" : ""}`}
                                      placeholderText="dd-mm-yyyy"
                                    />
                                    <span
                                      className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                      onClick={() => setIsContractStartOpen((prev) => !prev)}
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                    </span>
                                  </div>
                                )}
                              />
                              {errors.contractStartDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.contractStartDate.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractEndDate")}<span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="contractEndDate"
                                defaultValue={null}
                                render={({ field }) => (
                                  <div className={`position-relative date-picker-wrapper ${errors.contractEndDate ? "is-invalid-wrapper" : ""}`}>
                                    <DatePicker
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsContractEndOpen(false);
                                      }}
                                      open={isContractEndOpen}
                                      onInputClick={() => setIsContractEndOpen(true)}
                                      onClickOutside={() => setIsContractEndOpen(false)}
                                      dateFormat="dd-MM-yyyy"
                                      className={`form-control pe-2 py-2  ${errors.contractEndDate ? "is-invalid" : ""}`}
                                      placeholderText="dd-mm-yyyy"
                                      minDate={watch("contractStartDate")}
                                    />
                                    <span
                                      className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                      onClick={() => setIsContractEndOpen((prev) => !prev)}
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                    </span>
                                  </div>
                                )}
                              />
                              {errors.contractEndDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.contractEndDate.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md="5">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">{t("speciality")}</Form.Label>
                              <Controller
                                name="specialityIds"
                                control={control}
                                defaultValue={[]}
                                render={({ field }) => (
                                  <MultiSelect
                                    isMulti
                                    closeMenuOnSelect={false}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                    options={specialities.map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    value={specialities
                                      .filter(s => (field.value || []).includes(s.SpecialityId))
                                      .map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    classNamePrefix="react-select"
                                    onChange={(selected) => field.onChange(selected ? selected.map(o => o.value) : [])}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md="6">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractNotes")}
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={1}
                                placeholder={t("contractNotesPlaceholder")}
                                className="auto-expand-textarea"
                                {...register("contractNotes")}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-invoice"><FontAwesomeIcon icon={faCogs} /></span>
                      <span className="rf-header-text">
                        <span>{t("administration")}</span>
                        <span className="rf-header-subtitle">{t("administrationSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="2" lg="3" xl="3" className="mb-3 mb-lg-0">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour text-nowrap">
                            {t("fixedHours")}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            placeholder={("8")}
                            {...register("fixedHours", {
                              required: false,
                              pattern: {
                                value: /^[0-9]*[,.]?[0-9]{0,2}$/,
                                message:
                                  t("invalidHours") ||
                                  "Only numbers and a comma or dot are allowed",
                              },
                              setValueAs: (value) => {
                                if (value === null || value === undefined || value === "") return null;
                                if (typeof value === "number") return isNaN(value) ? null : value;
                                if (typeof value === "string" && value.trim() !== "") {
                                  const parsed = parseFloat(value.replace(",", "."));
                                  return isNaN(parsed) ? null : parsed;
                                }
                                return null;
                              },
                            })}
                            onKeyDown={(e) => {
                              const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                              ];
                              const isNumber = /^[0-9]$/.test(e.key);
                              const isCommaOrDot = e.key === "," || e.key === ".";
                              const value = e.currentTarget.value;

                              // Allow only one comma or dot
                              if (
                                !isNumber &&
                                !allowedKeys.includes(e.key) &&
                                !(
                                  isCommaOrDot &&
                                  !value.includes(",") &&
                                  !value.includes(".")
                                )
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {watch("employmentType") === "contract" && (
                        <Col md="2" lg="3">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour text-nowrap">
                              {t("hourlyRate")}
                            </Form.Label>
                            <InputGroup>
                              <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                              <Form.Control
                                type="text"
                                inputMode="decimal"
                                placeholder={("500")}
                                {...register("hourlyRate", {
                                  pattern: {
                                    value: /^\d{1,5}([,]\d{1,2})?$/,
                                    message:
                                      t("invalidRate"),
                                  },
                                  setValueAs: (value) => {
                                    if (value === null || value === undefined || value === "") return null;
                                    if (typeof value === "number") return isNaN(value) ? null : value;
                                    if (
                                      typeof value === "string" &&
                                      value.trim() !== ""
                                    ) {
                                      const parsed = parseFloat(value.replace(",", "."));
                                      return isNaN(parsed) ? null : parsed;
                                    }
                                    return null;
                                  },
                                })}
                                isInvalid={!!errors.hourlyRate}
                                onKeyDown={(e) => {
                                  const allowedKeys = [
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ];
                                  const isNumber = /^[0-9]$/.test(e.key);
                                  const isComma = e.key === ",";
                                  const value = e.currentTarget.value;

                                  if (
                                    !isNumber &&
                                    !allowedKeys.includes(e.key) &&
                                    !(isComma && !value.includes(","))
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.hourlyRate?.message}
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      )}
                      {watch("employmentType") === "permanent" && (
                        <>
                          <Col md="3" lg="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour text-nowrap">
                                {t("salary")}
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  inputMode="decimal"
                                  placeholder={("45000")}
                                  {...register("salary", {
                                    setValueAs: (value) => {
                                      if (value === null || value === undefined || value === "") return null;
                                      const parsed = parseFloat(String(value).replace(",", "."));
                                      return isNaN(parsed) ? null : parsed;
                                    },
                                  })}
                                  isInvalid={!!errors.salary}
                                  onKeyDown={(e) => {
                                    const allowedKeys = [
                                      "Backspace",
                                      "Delete",
                                      "ArrowLeft",
                                      "ArrowRight",
                                      "Tab",
                                    ];
                                    const isNumber = /^[0-9]$/.test(e.key);
                                    const isComma = e.key === ",";
                                    const value = e.currentTarget.value;

                                    if (
                                      !isNumber &&
                                      !allowedKeys.includes(e.key) &&
                                      !(isComma && !value.includes(","))
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.salary?.message}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md="3" lg="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("salaryPeriod")}
                              </Form.Label>
                              <Controller
                                name="salaryPeriod"
                                control={control}
                                defaultValue="monthly"
                                render={({ field, fieldState: { error } }) => {
                                  const salaryPeriodOptions = [
                                    { value: "monthly", label: t("salaryPerMonth") },
                                    { value: "weekly", label: t("salaryPerWeek") },
                                  ];

                                  return (
                                    <>
                                      <Select
                                        {...field}
                                        options={salaryPeriodOptions}
                                        value={
                                          salaryPeriodOptions.find((opt) => opt.value === field.value) || null
                                        }
                                        onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                        onBlur={field.onBlur}
                                        className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                        classNamePrefix="custom-select"
                                        placeholder={t("select")}
                                        noOptionsMessage={() => t("noOptionsAvailable")}
                                      />
                                      {error && (
                                        <div className="invalid-feedback d-block">{error.message}</div>
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </>
                      )}
                    </Row>
                    <Row>
                      <Col md="12" className="mb-3">
                        <label className="data-font-size data-font-colour">{t("weekdays")}</label>
                        <div className="d-flex flex-wrap gap-2">
                          {dayKeys.map((dayKey) => (
                            <div key={dayKey}>
                              <input
                                type="checkbox"
                                {...register(`weekdays.${dayKey}`)}
                                checked={selectedDays.includes(dayKey)}
                                readOnly
                                hidden
                              />
                              <Button
                                variant={
                                  selectedDays.includes(dayKey)
                                    ? "success"
                                    : "outline-secondary"
                                }
                                className="weekday-btn"
                                onClick={() => toggleDay(dayKey)}
                              >
                                {t(`days.${dayKey}`)}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
                <Card className="mt-2">
                  <Card.Body>
                    <Row className="justify-content-between align-items-center">
                      <Col xs="auto">
                        <Button variant="falcon-default" size="sm" type="button" onClick={handleDiscard}>
                          {t("cancel")}
                        </Button>
                      </Col>
                      <Col xs="auto">
                        {currentStep > 1 && (
                          <Button variant="falcon-default" size="sm" className="me-3" type="button" onClick={handleBack}>
                            {t("back")}
                          </Button>
                        )}
                        {currentStep < 4 && !otherStepsCompleted ? (
                          <Button variant="primary" size="sm" type="button" onClick={handleNext}>
                            {t("next")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                          </Button>
                        ) : (
                          <Button data-tour="save-btn" variant="primary" size="sm" type="button" onClick={handleSave} disabled={isLoading}>
                            {isLoading ? t("saving") : (<>{t("save")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" /></>)}
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
            </Col>
          </Row>
        </Form>
      </FormProvider>

      <Modal show={showSubModal} onHide={() => setShowSubModal(false)} backdrop="static">
        <Modal.Header>
          <Modal.Title>
            {subModalData.isTrialWarning ? t("trialSubscription") : t("subscriptionLimitReached")}
          </Modal.Title>
          <FalconCloseButton onClick={() => setShowSubModal(false)} />
        </Modal.Header>
        <Modal.Body>
          <p>{subModalData.message}</p>
        </Modal.Body>
        <Modal.Footer>
          {subModalData.isTrialWarning ? (
            // Trial under limit — user can continue or upgrade
            <>
              <Button
                variant="falcon-default"
                size="sm"
                onClick={() => {
                  setShowSubModal(false);
                  submitUser(methods.getValues());
                }}
              >
                {t("continueButton")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setShowSubModal(false);
                  navigate(`${paths.mySubscription}?culture=${culture}`);
                }}
              >
                {t("upgradeButton")}
              </Button>
            </>
          ) : (
            // Limit reached — show correct upgrade action per plan
            <>
              <Button
                variant="falcon-default"
                size="sm"
                onClick={() => setShowSubModal(false)}
              >
                {t("cancelButton")}
              </Button>

              {/* Standard plan: contact us to upgrade to Enterprise */}
              {subModalData.showEmail ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setShowSubModal(false);
                    window.open(process.env.REACT_APP_ENTERPISE_UPGRADE_URL, "_blank");
                  }}
                >
                  {t("contactUsButton")}
                </Button>
              ) : (
                // Trial / Basic: go to subscription page
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setShowSubModal(false);
                    navigate(`${paths.mySubscription}?culture=${culture}`);
                  }}
                >
                  {t("upgradeButton")}
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NewUser;
